import { ComponentType, lazy } from "react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

const RELOAD_KEY = "cm_chunk_reloads";
const MAX_RELOADS = 2;

/**
 * A browser holding an old index.html asks for a chunk hash the SWA no longer
 * serves. Two surface forms, both CM2-14G:
 *   - the fetch 404s           -> "Failed to fetch dynamically imported module"
 *   - the SPA fallback answers -> "'text/html' is not a valid JavaScript MIME type" (CM2-1MR)
 * Anything else (a real throw inside the module) must NOT trigger a reload, or a
 * genuinely broken build would put every user in a reload loop.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  const msg = (error as Error)?.message ?? "";
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("is not a valid JavaScript MIME type")
  );
};

// sessionStorage throws outright in sandboxed iframes and with site data blocked
// (B302 / CM2-2NW), so every access is guarded and falls back to "no reloads yet".
const readReloads = (): number => {
  try {
    return Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeReloads = (count: number) => {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(count));
  } catch {
    // no storage, no counter — the MAX_RELOADS cap degrades to "reload every time",
    // which is still bounded by the user leaving the page.
  }
};

const clearReloads = () => {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    /* see writeReloads */
  }
};

/**
 * `React.lazy` with the stale-chunk case handled instead of thrown at the user.
 *
 * Today a chunk 404 lands on the nearest ErrorBoundary, which renders
 * "Something went wrong..." over the whole route — and two paying accounts have
 * cancelled within minutes of that screen (features/sentry/quality.md). A reload
 * fetches a fresh index.html with the current hashes and the user carries on.
 *
 * Order: retry once for a transient network blip, then reload, at most twice per
 * tab. Any successful load clears the counter, so a tab that survives several
 * deploys keeps its budget.
 */
export const loadWithRetry = async <T>(
  factory: () => Promise<T>
): Promise<T> => {
  try {
    const module = await factory();
    clearReloads();
    return module;
  } catch (error) {
    if (!isChunkLoadError(error)) throw error;

    // A dropped request and a chunk that is genuinely gone look identical here,
    // so spend a cheap retry before spending a page reload.
    try {
      const module = await factory();
      clearReloads();
      return module;
    } catch (retryError) {
      if (!isChunkLoadError(retryError)) throw retryError;

      const reloads = readReloads();
      if (reloads < MAX_RELOADS) {
        writeReloads(reloads + 1);
        window.location.reload();
        // Hold the promise open so Suspense keeps the loading state up rather
        // than flashing the error screen during the unload.
        return new Promise<never>(() => {});
      }

      // Reloading did not help — this is not a stale tab. Let the ErrorBoundary
      // have it so the user sees something other than a spinner.
      throw retryError;
    }
  }
};

export const lazyWithRetry = (factory: () => Promise<{ default: AnyComponent }>) =>
  lazy(() => loadWithRetry(factory));

export default lazyWithRetry;
